import {
    confirmResetPassword,
    confirmSignIn,
    fetchUserAttributes,
    forgetDevice,
    getCurrentUser,
    rememberDevice,
    resetPassword,
    signIn,
    signOut,
    signUp,
    updatePassword,
} from "@aws-amplify/auth"
import {
    cognitoCredentialsProvider,
    cognitoUserPoolsTokenProvider,
} from "@aws-amplify/auth/cognito"
import { Amplify, fetchAuthSession } from "@aws-amplify/core"
import type { AuthTokens } from "@aws-amplify/core"

type Callback = {
    onFailure(error: unknown): void
    onSuccess(result?: unknown): void
}

type AuthFlowType =
    | "USER_AUTH"
    | "USER_SRP_AUTH"
    | "CUSTOM_WITH_SRP"
    | "CUSTOM_WITHOUT_SRP"
    | "USER_PASSWORD_AUTH"

export class CognitoUserAttribute {
    constructor(
        private readonly name: string,
        private readonly value: string,
    ) {}

    getName(): string {
        return this.name
    }

    getValue(): string {
        return this.value
    }
}

export class CognitoUser {
    challengeName?: string
    challengeParam: { requiredAttributes: string[] } = {
        requiredAttributes: [],
    }

    constructor(
        private readonly username: string,
        missingAttributes: string[] = [],
        requiresNewPassword = false,
    ) {
        if (requiresNewPassword) {
            this.challengeName = "NEW_PASSWORD_REQUIRED"
            this.challengeParam.requiredAttributes = missingAttributes
        }
    }

    getUsername(): string {
        return this.username
    }

    getUserAttributes(
        callback: (error: unknown, attributes?: CognitoUserAttribute[]) => void,
    ): void {
        ensureAmplifyConfigured()
        fetchUserAttributes()
            .then((attributes) =>
                callback(
                    null,
                    Object.entries(attributes).map(
                        ([name, value]) =>
                            new CognitoUserAttribute(name, value || ""),
                    ),
                ),
            )
            .catch((error) => callback(normalizeError(error)))
    }

    completeNewPasswordChallenge(
        newPassword: string,
        attributes: Record<string, string>,
        callback: Callback,
    ): void {
        ensureAmplifyConfigured()
        confirmSignIn({
            challengeResponse: newPassword,
            options: { userAttributes: attributes },
        })
            .then(callback.onSuccess)
            .catch((error) => callback.onFailure(normalizeError(error)))
    }

    setDeviceStatusRemembered(callback: Callback): void {
        ensureAmplifyConfigured()
        rememberDevice()
            .then(callback.onSuccess)
            .catch((error) => callback.onFailure(normalizeError(error)))
    }

    setDeviceStatusNotRemembered(callback: Callback): void {
        ensureAmplifyConfigured()
        forgetDevice()
            .then(callback.onSuccess)
            .catch((error) => callback.onFailure(normalizeError(error)))
    }
}

let authFlowType: AuthFlowType = "USER_SRP_AUTH"
let amplifyConfigured = false
const currentCredentialsCacheWindowMs = 5000
let cachedCurrentCredentials:
    | ({ identityId: string } & Record<string, unknown>)
    | null = null
let cachedCurrentCredentialsAt = 0
let currentCredentialsPromise: Promise<
    { identityId: string } & Record<string, unknown>
> | null = null

function resetCurrentCredentialsCache(): void {
    cachedCurrentCredentials = null
    cachedCurrentCredentialsAt = 0
    currentCredentialsPromise = null
}

export function configureAmplify(): void {
    const authConfig = {
        Cognito: {
            userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || "",
            userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || "",
            identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID || "",
        },
    }
    cognitoUserPoolsTokenProvider.setAuthConfig(authConfig)
    Amplify.configure(
        {
            Auth: authConfig,
        },
        {
            Auth: {
                tokenProvider: cognitoUserPoolsTokenProvider,
                credentialsProvider: cognitoCredentialsProvider,
            },
        },
    )
    amplifyConfigured = true
}

function ensureAmplifyConfigured(): void {
    const cognitoConfig = Amplify.getConfig().Auth?.Cognito
    if (
        !amplifyConfigured ||
        !cognitoConfig?.userPoolId ||
        !cognitoConfig?.userPoolClientId
    ) {
        configureAmplify()
    }
}

export const UnauthenticatedMessage = "The user is not authenticated"

export function isUnauthenticatedError(error: any): boolean {
    const msg = error?.message || error?.code || error || ""
    return (
        msg === UnauthenticatedMessage ||
        msg === "User needs to be authenticated to call this API." ||
        msg === "Auth UserPool not configured." ||
        msg === "Unable to get user session following successful sign-in." ||
        error?.name === "UnexpectedSignInInterruptionException"
    )
}

function normalizeError(error: any): any {
    if (isUnauthenticatedError(error)) {
        return new Error(UnauthenticatedMessage)
    }
    if (error && !error.code && error.name) {
        error.code = error.name
    }
    return error
}

async function withLegacyError<T>(promise: Promise<T>): Promise<T> {
    try {
        return await promise
    } catch (error) {
        throw normalizeError(error)
    }
}

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

async function currentTokensWithRetry(retries = 5): Promise<AuthTokens> {
    let lastError: unknown
    for (let i = 0; i < retries; i++) {
        try {
            const tokens = await cognitoUserPoolsTokenProvider.getTokens({
                forceRefresh: false,
            })
            if (tokens?.idToken) {
                return tokens
            }
            lastError = new Error(UnauthenticatedMessage)
        } catch (error) {
            lastError = error
        }
        await sleep(100)
    }
    throw normalizeError(lastError)
}

async function currentUserAfterSignIn(
    fallbackUsername: string,
): Promise<CognitoUser | null> {
    for (let i = 0; i < 5; i++) {
        try {
            const tokens = await currentTokensWithRetry(1)
            const username = tokens.idToken?.payload["cognito:username"]
            return new CognitoUser(String(username || fallbackUsername))
        } catch (error) {
            await sleep(100)
        }
    }
    return null
}

const Auth = {
    configure(config: { authenticationFlowType?: AuthFlowType }): void {
        if (config.authenticationFlowType) {
            authFlowType = config.authenticationFlowType
        }
    },

    async currentSession() {
        ensureAmplifyConfigured()
        const tokens = await currentTokensWithRetry()
        const jwtToken = tokens.idToken?.toString()
        const payload = tokens.idToken?.payload
        if (!jwtToken) {
            throw new Error(UnauthenticatedMessage)
        }
        return {
            getIdToken: () => ({
                getJwtToken: () => jwtToken,
                payload,
            }),
        }
    },

    async currentCredentials() {
        ensureAmplifyConfigured()
        const now = Date.now()
        if (
            cachedCurrentCredentials &&
            now - cachedCurrentCredentialsAt < currentCredentialsCacheWindowMs
        ) {
            return cachedCurrentCredentials
        }
        if (currentCredentialsPromise) {
            return currentCredentialsPromise
        }
        currentCredentialsPromise = withLegacyError(fetchAuthSession())
            .then((session) => {
                if (!session.identityId) {
                    throw new Error("The user has no identity ID")
                }
                const credentials = {
                    ...session.credentials,
                    identityId: session.identityId,
                }
                cachedCurrentCredentials = credentials
                cachedCurrentCredentialsAt = Date.now()
                return credentials
            })
            .finally(() => {
                currentCredentialsPromise = null
            })
        return currentCredentialsPromise
    },

    async currentAuthenticatedUser(): Promise<CognitoUser> {
        ensureAmplifyConfigured()
        const user = await withLegacyError(getCurrentUser())
        return new CognitoUser(user.username)
    },

    async signIn(username: string, password: string): Promise<CognitoUser> {
        ensureAmplifyConfigured()
        let result
        try {
            result = await signIn({
                username,
                password,
                options: { authFlowType },
            })
        } catch (error) {
            const normalizedError = normalizeError(error)
            if (isUnauthenticatedError(normalizedError)) {
                const user = await currentUserAfterSignIn(username)
                if (user) {
                    return user
                }
            }
            throw normalizedError
        }
        if (result.nextStep.signInStep === "RESET_PASSWORD") {
            const error = new Error("Password reset required")
            error.name = "PasswordResetRequiredException"
            throw normalizeError(error)
        }
        if (
            result.nextStep.signInStep ===
            "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
        ) {
            return new CognitoUser(
                username,
                result.nextStep.missingAttributes || [],
                true,
            )
        }
        if (!result.isSignedIn) {
            throw new Error(
                `Unsupported sign-in step: ${result.nextStep.signInStep}`,
            )
        }
        await currentTokensWithRetry()
        resetCurrentCredentialsCache()
        return new CognitoUser(username)
    },

    signOut(): Promise<void> {
        ensureAmplifyConfigured()
        resetCurrentCredentialsCache()
        return withLegacyError(signOut())
    },

    async signUp(input: {
        username: string
        password: string
        attributes: Record<string, string>
        validationData?: Record<string, string>
    }): Promise<{ user: CognitoUser }> {
        ensureAmplifyConfigured()
        await withLegacyError(
            signUp({
                username: input.username,
                password: input.password,
                options: {
                    userAttributes: input.attributes,
                    validationData: input.validationData,
                },
            }),
        )
        return { user: new CognitoUser(input.username) }
    },

    forgotPassword(username: string, clientMetadata?: Record<string, string>) {
        ensureAmplifyConfigured()
        return withLegacyError(
            resetPassword({
                username,
                options: { clientMetadata },
            }),
        )
    },

    forgotPasswordSubmit(
        username: string,
        confirmationCode: string,
        newPassword: string,
    ): Promise<void> {
        ensureAmplifyConfigured()
        return withLegacyError(
            confirmResetPassword({
                username,
                confirmationCode,
                newPassword,
            }),
        )
    },

    changePassword(
        _user: CognitoUser,
        oldPassword: string,
        newPassword: string,
    ): Promise<void> {
        ensureAmplifyConfigured()
        return withLegacyError(updatePassword({ oldPassword, newPassword }))
    },
}

export default Auth
